import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';

export type ModalAction = {
  content: string;
  onAction: () => void;
  destructive?: boolean;
  loading?: boolean;
  disabled?: boolean;
};

type ModalProps = {
  open: boolean;
  onClose: () => void;
  heading?: string;
  // For modals without a visible heading.
  accessibilityLabel?: string;
  size?: 'base' | 'large';
  primaryAction?: ModalAction;
  secondaryActions?: ModalAction[];
  children?: ReactNode;
};

// Replaces Polaris React's Modal: drives s-modal (opened imperatively) from an
// `open` prop, and reports closes from Escape or its close button via onClose.
const Modal: FC<ModalProps> = ({
  open,
  onClose,
  heading,
  accessibilityLabel,
  size = 'base',
  primaryAction,
  secondaryActions = [],
  children,
}) => {
  const ref = useRef<HTMLElementTagNameMap['s-modal']>(null);
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);
  // Keeps the content during the closing animation; Polaris unmounted it when closed.
  const [rendered, setRendered] = useState(open);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Listeners are added directly rather than as onHide/onAfterHide props: the
  // bundle clears its prop handlers on unmount but afterhide can still arrive
  // when a modal is unmounted while closing, which then throws.
  useEffect(() => {
    const modal = ref.current;
    if (!modal) return;
    const handleHide = () => {
      // Also fires after hideOverlay() below; only report closes the user started.
      if (openRef.current) {
        openRef.current = false;
        onCloseRef.current();
      }
    };
    const handleAfterHide = () => {
      if (!openRef.current) setRendered(false);
    };
    modal.addEventListener('hide', handleHide);
    modal.addEventListener('afterhide', handleAfterHide);
    return () => {
      modal.removeEventListener('hide', handleHide);
      modal.removeEventListener('afterhide', handleAfterHide);
    };
  }, []);

  useEffect(() => {
    openRef.current = open;
    if (!open) {
      ref.current?.hideOverlay();
      return;
    }
    setRendered(true);
    ref.current?.showOverlay();
    // On the first open after a page load, Escape closes the native dialog
    // without s-modal sending hide, which would leave `open` stuck at true.
    const dialog = ref.current?.shadowRoot?.querySelector('dialog');
    const handleClose = () => {
      if (openRef.current) {
        openRef.current = false;
        onCloseRef.current();
      }
    };
    dialog?.addEventListener('close', handleClose);
    return () => dialog?.removeEventListener('close', handleClose);
  }, [open]);

  return (
    <s-modal
      ref={ref}
      heading={heading}
      accessibilityLabel={accessibilityLabel}
      size={size}
    >
      {(open || rendered) && (
        <>
          {children}
          {primaryAction && (
            <s-button
              slot='primary-action'
              variant='primary'
              tone={primaryAction.destructive ? 'critical' : 'auto'}
              loading={primaryAction.loading}
              disabled={primaryAction.disabled}
              onClick={primaryAction.onAction}
            >
              {primaryAction.content}
            </s-button>
          )}
          {secondaryActions.map((action) => (
            <s-button
              key={action.content}
              slot='secondary-actions'
              tone={action.destructive ? 'critical' : 'auto'}
              loading={action.loading}
              disabled={action.disabled}
              onClick={action.onAction}
            >
              {action.content}
            </s-button>
          ))}
        </>
      )}
    </s-modal>
  );
};

export default Modal;
